import { TimelineItemBoxProps } from "@/app/activity/components/timeline/activityTimeline/type"
import { createTextDot } from "@/app/activity/components/timeline/activityTimeline/utils"
import { createActivityStore, createSessionStore } from "@/app/activity/store"

import classNames from "classnames"
import { drag } from "d3-drag"
import { select } from "d3-selection"
import { useEffect, useMemo, useRef, useState } from "react"

import "./timelineItemBox.scss"
import { Session } from "@/types/session"
import { isNil } from "lodash"

export default function TimelineItemBox({
  id,
  height,
  editable,
  hoverable,
  name,
  width,
  fillColor,
  strokeColor,
  startX,
  startY,
  onDragStart,
  onDrag,
  onDragEnd,
  onClick,
  className,
  remainRightArea,
  isSession = false,
  ...props
}: TimelineItemBoxProps) {
  const BOX_TEXT_Y_ADJUST = 4
  const BOX_HANDLE_WIDTH = 5
  const BOX_WIDTH = 14
  const FONT_SIZE = 12

  const [isHovered, setIsHovered] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [isIntersect, setIsIntersect] = useState(true)

  const [boxName, setBoxName] = useState(name)

  const dragLeft = useRef<SVGRectElement>(null)
  const dragRight = useRef<SVGRectElement>(null)
  const rootRef = useRef<SVGGElement>(null)

  const { hoveredActivity, setHoveredActivity } = createActivityStore((state) => ({
    hoveredActivity: state.hoveredActivityId,
    setHoveredActivity: state.setHoveredActivity,
  }))

  const { setHoveredSession, hoveredSession, selectedSection } = createSessionStore((state) => ({
    setHoveredSession: state.setHoveredSession,
    hoveredSession: state.hoveredSession,
    selectedSection: state.temporarySession,
  }))

  const transform = useMemo(() => {
    if (!isNil(startX) && !isNil(startY)) {
      return `translate(${startX + (remainRightArea || 0)}, ${startY})`
    }
  }, [startX, startY, remainRightArea])

  const scrollToVisible = () => {
    const rootElement = document.querySelector(".SessionTimelineContWrapper") as HTMLElement

    if (rootElement && !isIntersect) {
      rootElement.scrollTo({
        top: startY - 5,
      })
    }
  }

  const onMouseOver = () => {
    if (hoverable) {
      setIsHovered(() => true)
      setHoveredActivity(id)

      if (isSession) {
        setHoveredSession({
          dateCreated: props.dateCreated,
          dateFinish: props.dateFinish,
          dateStart: props.dateStart,
          idfSession: props.idfSession,
          idfSessionType: props.idfSessionType,
          name: (props as unknown as Session).name,
          playerCount: props.playerCount,
          type: props.type,
          duration: props.duration,
        } as Session)
      }
    }
  }

  const onMouseLeave = () => {
    if (hoverable) {
      setHoveredActivity(undefined)
      setIsHovered(false)

      if (isSession) {
        setHoveredSession(undefined)
      }
    }
  }

  const setDragEvent = () => {
    if (dragLeft.current) {
      let startClientX = 0
      select(dragLeft.current as Element).call(
        drag()
          .on("start", (event) => {
            setIsDragging(true)
            startClientX = event.sourceEvent.clientX
            onDragStart?.(event)
          })
          .on("drag", (event) => {
            setIsDragging(true)
            const currentClientX = event.sourceEvent.clientX
            const moveX = currentClientX - startClientX

            onDrag?.({
              id,
              direction: "left",
              moveX,
              startX,
              endX: startX + width,
              event: event.sourceEvent,
            })
          })
          .on("end", (event) => {
            setIsDragging(false)
            const currentClientX = event.sourceEvent.clientX
            const moveX = currentClientX - startClientX

            onDragEnd?.({
              id,
              direction: "left",
              moveX,
              startX,
              endX: startX + width,
              event: event.sourceEvent,
            })
          }),
      )
    }

    if (dragRight.current) {
      let startClientX = 0
      select(dragRight.current as Element).call(
        drag()
          .on("start", (event) => {
            setIsDragging(true)
            startClientX = event.sourceEvent.clientX
            onDragStart?.(event)
          })
          .on("drag", (event) => {
            setIsDragging(true)
            const currentClientX = event.sourceEvent.clientX
            const moveX = currentClientX - startClientX

            onDrag?.({
              id,
              direction: "right",
              moveX,
              startX,
              endX: startX + width,
              event: event.sourceEvent,
            })
          })
          .on("end", (event) => {
            setIsDragging(false)
            const currentClientX = event.sourceEvent.clientX
            const moveX = currentClientX - startClientX

            onDragEnd?.({
              id,
              direction: "right",
              moveX,
              startX,
              endX: startX + width,
              event: event.sourceEvent,
            })
          }),
      )
    }
  }

  useEffect(() => {
    if (!isSession) {
      return
    }

    const containerEle = document.querySelector(".SessionTimelineContWrapper") as HTMLElement

    if (containerEle) {
      const observer = new IntersectionObserver(
        (entries) => {
          setIsIntersect(() => entries[0].isIntersecting)
        },
        {
          root: containerEle,
        },
      )

      observer.observe(rootRef.current as Element)

      return () => {
        observer.disconnect()
      }
    }
  }, [isSession, selectedSection])

  useEffect(() => {
    setBoxName(() => name)
  }, [name])

  useEffect(() => {
    select(`#_${id}`).call(createTextDot, width, name)
  }, [width, name, id])

  useEffect(() => {
    if (dragLeft.current && dragRight.current) {
      setDragEvent()
    }
  }, [dragLeft.current, dragRight.current])

  useEffect(() => {
    if (hoveredActivity && hoveredActivity === id) {
      setIsHovered(true)
    } else {
      setIsHovered(false)
    }
  }, [hoveredActivity])

  useEffect(() => {
    if (selectedSection && selectedSection.idfSession === props.idfSession) {
      scrollToVisible()
    }

    if (hoveredSession && hoveredSession.idfSession === props.idfSession && !selectedSection) {
      scrollToVisible()
    }
  }, [hoveredSession, selectedSection])

  return (
    <g
      className={classNames("TimelineItemBoxCont", className, { editable, hovered: isHovered })}
      transform={transform}
      onMouseOver={onMouseOver}
      onMouseEnter={onMouseOver}
      onMouseMove={onMouseOver}
      onMouseLeave={onMouseLeave}
      ref={rootRef}
      onClick={() => {
        onClick?.({ ...props, name, width, startX, startY, height })
      }}>
      <rect
        className={classNames({ editable, hovered: isHovered })}
        width={width || BOX_WIDTH}
        height={height || 0}
        rx="0.2em"
        ry="0.2em"
        fill={fillColor}
        style={{ stroke: strokeColor, strokeWidth: 1 }}
      />

      <g>
        <text
          id={`_${id}`}
          className={classNames(editable)}
          x={(width || BOX_WIDTH || 0) / 2}
          y={(height || 0) / 2 + BOX_TEXT_Y_ADJUST}
          alignmentBaseline="middle"
          fontSize={FONT_SIZE}
          textAnchor="middle"
          fill={strokeColor}
          stroke={strokeColor}
          strokeWidth={0.5}>
          {boxName}
        </text>
      </g>

      {editable && (
        <g style={{ visibility: (editable && isHovered) || isDragging ? "visible" : "hidden" }}>
          <rect
            ref={dragLeft}
            className="handle"
            width={BOX_HANDLE_WIDTH}
            height={height}
            x={0}
            rx="5"
            ry="3"
          />

          <rect
            ref={dragRight}
            className="handle"
            width={BOX_HANDLE_WIDTH}
            height={height}
            x={(width || 0) - BOX_HANDLE_WIDTH}
            rx="5"
            ry="3"
          />
        </g>
      )}
    </g>
  )
}
